import React, { useState } from 'react';
import { generateSmallSteps } from '../services/geminiService';
import { SmallStepPlan } from '../types';
import { Footprints, RefreshCw, ArrowDown, Play, CheckCircle } from 'lucide-react';
import { useUser } from '../context/UserContext';

const KaizenSmallSteps: React.FC = () => {
  const [goal, setGoal] = useState('');
  const [plan, setPlan] = useState<SmallStepPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const { addXp } = useUser();

  const handleBreakdown = async () => {
    if (!goal.trim()) return;
    setLoading(true);
    setPlan(null);
    try {
      const data = await generateSmallSteps(goal);
      setPlan(data);
      addXp(40, 'Created Kaizen Steps');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-green-50 text-green-600 rounded-full mb-4">
          <Footprints size={32} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">Small Step Builder</h1>
        <p className="text-nunchi-600">
          The brain resists big changes. The secret to Kaizen is making the step so small you cannot fail.
        </p>
      </header>

      {!plan && !loading && (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-nunchi-200">
            <label className="block text-sm font-bold text-nunchi-500 uppercase tracking-wider mb-3">
                What big goal is overwhelming you?
            </label>
            <textarea
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="e.g., Write a novel, Get fit, Organize my entire house..."
              className="w-full h-32 bg-nunchi-50 rounded-xl p-4 border-none focus:ring-2 focus:ring-green-200 outline-none resize-none text-nunchi-800 placeholder:text-nunchi-300 mb-4"
            />
            <button
              onClick={handleBreakdown}
              disabled={!goal.trim()}
              className="w-full bg-green-700 text-white py-4 rounded-xl font-bold hover:bg-green-800 transition-all flex justify-center items-center gap-2 disabled:opacity-50"
            >
              <Footprints size={18} /> Break It Down
            </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-20">
             <div className="flex justify-center mb-4 text-green-300 animate-bounce">
                <Footprints size={48} />
             </div>
             <p className="font-serif italic text-green-700">Making it smaller...</p>
        </div>
      )}

      {plan && (
        <div className="animate-fadeIn space-y-6">
            <div className="bg-white p-6 rounded-xl border border-nunchi-200 shadow-sm">
                <h3 className="text-green-600 font-bold uppercase tracking-wider text-xs mb-2">The Kaizen Mindset</h3>
                <p className="text-nunchi-800 italic">"{plan.motivation}"</p>
            </div>

            <div className="relative pl-8 space-y-8">
                {/* Vertical Line */}
                <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-green-100"></div>

                {plan.steps.map((step, idx) => (
                    <div key={idx} className="relative">
                        <div className="absolute -left-8 top-1 bg-white border-2 border-green-200 text-green-600 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">
                            {idx + 1}
                        </div>
                        <div className="bg-nunchi-50 p-4 rounded-lg border border-nunchi-100">
                            <p className="text-nunchi-800">{step}</p>
                        </div>
                        {idx < plan.steps.length - 1 && (
                            <ArrowDown size={16} className="text-green-200 mx-auto mt-2" />
                        )}
                    </div>
                ))}
            </div>

            <div className="bg-green-600 text-white p-6 rounded-xl shadow-lg transform hover:scale-[1.02] transition-transform">
                <h3 className="font-bold uppercase tracking-wider text-xs mb-2 flex items-center gap-2 text-green-100">
                    <Play size={14} /> Your First Action (Do this NOW)
                </h3>
                <p className="text-xl font-bold">{plan.firstAction}</p>
                <div className="mt-4 flex items-center gap-2 text-sm text-green-100 bg-green-700/50 p-2 rounded">
                    <CheckCircle size={16} /> Takes less than 1 minute
                </div>
            </div>

            <div className="text-center">
                <button 
                  onClick={() => { setPlan(null); setGoal(''); }}
                  className="text-green-600 hover:text-green-800 font-bold flex items-center gap-2 mx-auto"
                >
                    <RefreshCw size={16} /> New Breakdown
                </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default KaizenSmallSteps;